import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { TenaxTask } from "./taskGroups";

export interface TaskRowHelpers {
  areaName: (id: number | null) => string | undefined;
  ownerName: (id: number | null) => string;
  stepsOf: (id: number) => { done: number; total: number };
}

// One task in a list (tasks page and Today screen). One tap on the box
// closes the task, even with open steps (D-031): the notice says how many
// are left and offers to undo. The title opens the task.
export const TaskRow = ({
  task,
  areaName,
  ownerName,
  stepsOf,
}: { task: TenaxTask } & TaskRowHelpers) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const steps = stepsOf(task.id);
  const isDone = task.status === "done";
  const area = areaName(task.area_id);

  const setStatus = async (status: string, previousStatus: string) => {
    await dataProvider.update("tasks", {
      id: task.id,
      data: { status },
      previousData: { ...task, status: previousStatus },
    });
    await queryClient.invalidateQueries({ queryKey: ["tasks"] });
    await queryClient.invalidateQueries({ queryKey: ["today_items"] });
  };

  const toggleDone = async () => {
    try {
      if (isDone) {
        await setStatus("todo", "done");
        return;
      }
      const previousStatus = task.status;
      await setStatus("done", previousStatus);
      const openSteps = steps.total - steps.done;
      toast(translate("tenax.today.closed"), {
        description:
          openSteps > 0
            ? translate("tenax.today.open_steps", { smart_count: openSteps })
            : undefined,
        action: {
          label: translate("ra.action.undo"),
          onClick: () => {
            setStatus(previousStatus, "done").catch((error: unknown) =>
              notify(error instanceof Error ? error.message : String(error), {
                type: "error",
              }),
            );
          },
        },
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };

  return (
    <li className="flex items-start gap-3 px-3 py-2">
      <Checkbox
        className="mt-1 size-5"
        checked={isDone}
        disabled={task.status === "cancelled"}
        onCheckedChange={toggleDone}
        aria-label={`${translate("tenax.tasks.actions.done")}: ${task.text}`}
      />
      <Link
        to={`/tasks/${task.id}/show`}
        className="flex flex-1 flex-col gap-1"
      >
        <span
          className={
            isDone ? "line-through text-muted-foreground" : "font-medium"
          }
        >
          {task.text}
        </span>
        <span className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
          {task.about && <span className="text-foreground">{task.about}</span>}
          {area && <Badge variant="secondary">{area}</Badge>}
          {task.priority === 1 && (
            <Badge variant="destructive">
              {translate("tenax.tasks.priority.1")}
            </Badge>
          )}
          <span>{ownerName(task.sales_id)}</span>
          {task.due_date && (
            <span>{new Date(task.due_date).toLocaleDateString("it-IT")}</span>
          )}
          {steps.total > 0 && (
            <span>
              {translate("tenax.tasks.checklist.progress", {
                done: steps.done,
                total: steps.total,
              })}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
};

export const TaskGroup = ({
  title,
  tasks,
  ...helpers
}: { title: string; tasks: TenaxTask[] } & TaskRowHelpers) => (
  <section className="flex flex-col gap-1">
    <h2 className="text-muted-foreground text-sm font-semibold uppercase">
      {title}
    </h2>
    <ul className="flex flex-col divide-y rounded-md border">
      {tasks.map((task) => (
        <TaskRow key={task.id} task={task} {...helpers} />
      ))}
    </ul>
  </section>
);
